/**
 * MarcacionView - Kiosk de marcacion de asistencia v1 (PIN).
 *
 * Flujo de 4 eventos por jornada:
 *   entrada -> inicio_almuerzo -> fin_almuerzo -> salida
 *
 * @module presentation/views/MarcacionView
 */

// SIN-VUELTA: es el kiosco donde el colaborador marca su jornada con PIN. Sale de ahi marcando, no navegando; un enlace al panel lo sacaria de su unica pantalla.
// Lo declara la pantalla y no una lista en el build: la razon tiene que estar
// donde alguien la lea el dia que se pregunte por que esta no tiene salida.

import { AsistenciaApiClient } from '../../infrastructure/api/AsistenciaApiClient.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';
import { antesDeMarcar, recordarMarca } from '../../application/asistencia/alertaDeMarca.js';
import { PIN_MAX, agregarDigito, puedeEnviar, seEnviaSolo, AVISO_CORTO } from '../../application/asistencia/pinDelKiosko.js';
import { respuestaDelPin } from '../../application/asistencia/respuestaDelPin.js';
// Deuda 4: el escape vivia copiado dentro de una funcion; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { formatearFecha } from '../shared/fechaColombiana.js';
// EL AVISO ANTES DE UNA MARCA REPETIDA (KIOSK-ALERTA-DUPLICADO-T1). Este aparato recuerda sus marcas de los ultimos
// minutos (localStorage, con try/catch: sin almacenamiento el kiosko marca igual, solo sin la capa local).
const CLAVE_RECIENTES = 'nx_kiosk_recientes';
function leerRecientes() {
  try { const l = JSON.parse(localStorage.getItem(CLAVE_RECIENTES) || '[]'); return Array.isArray(l) ? l : []; } catch { return []; }
}
function guardarReciente(colaboradorId, tipo) {
  try { localStorage.setItem(CLAVE_RECIENTES, JSON.stringify(recordarMarca(leerRecientes(), { colaboradorId, tipo, t: Date.now() }))); } catch { /* sin almacenamiento */ }
}

/** Si la marca parece repetida (o no es la que toca), pregunta antes de pedir el PIN; si no, sigue directo. */
function conAviso(container, { colaboradorId, colaboradorNombre, accion, kiosk }, seguir) {
  const { preguntar, lineas } = antesDeMarcar({ colaboradorId, accion, ahora: Date.now(), recientes: leerRecientes(), kiosk });
  if (!preguntar) { seguir(); return; }
  const overlay = document.createElement('div');
  overlay.className = 'kiosk-pin-overlay';
  overlay.setAttribute('data-kiosk-aviso', '');
  overlay.innerHTML = `
    <div class="kiosk-pin-box kiosk-accion-sel-box" role="alertdialog" aria-labelledby="kiosk-aviso-t">
      <div class="kiosk-pin-header">
        <span class="kiosk-pin-nombre">${esc(colaboradorNombre)}</span>
        <span id="kiosk-aviso-t" style="color:var(--color-text-muted);font-size:14px">¿Seguro que quieres registrar otra marca?</span>
      </div>
      ${lineas.map((l) => `<p class="kiosk-aviso-linea" style="margin:.4rem 0;font-size:15px">${esc(l)}</p>`).join('')}
      <div class="kiosk-accion-sel-btns">
        <button class="kiosk-accion-sel-btn" data-aviso-si>Sí, registrar</button>
      </div>
      <button class="kiosk-pin-cancel" data-aviso-no>Cancelar</button>
    </div>`;
  container.appendChild(overlay);
  overlay.querySelector('[data-aviso-si]').onclick = () => { overlay.remove(); seguir(); };
  overlay.querySelector('[data-aviso-no]').onclick = () => overlay.remove();
}


function initials(nombre) {
  return nombre.split(' ').slice(0, 2).map(p => p[0] ?? '').join('').toUpperCase();
}

function avatarColor(nombre) {
  const colors = ['#1a6b3c','#1a4a8c','#7b2d8b','#b5451b','#0d6e6e','#6b4a1a','#1a3d6b'];
  let h = 0;
  for (const c of nombre) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  return colors[Math.abs(h) % colors.length];
}

const ACCION_INFO = {
  entrada:         { label: 'Entrada',         icon: '⬆', cls: 'accion-entrada'  },
  inicio_almuerzo: { label: 'Inicio almuerzo',  icon: '🍽', cls: 'accion-almuerzo' },
  fin_almuerzo:    { label: 'Volver al trabajo',icon: '▶', cls: 'accion-regreso'  },
  salida:          { label: 'Salida',           icon: '⬇', cls: 'accion-salida'   },
};

/**
 * Dada la `proximaAccion` del colaborador, retorna la lista de opciones
 * que se pueden marcar en este momento.
 *
 * Después de "entrada" el colaborador puede elegir:
 *   - Inicio almuerzo  (si no lo ha hecho)
 *   - Salida directa   (si quiere salir sin almorzar)
 *
 * Después de "inicio_almuerzo":
 *   - Fin almuerzo  (siempre)
 *
 * Después de "fin_almuerzo":
 *   - Salida
 *
 * Si la jornada ya esta completa (proximaAccion === 'cerrado' o null),
 * retorna lista vacia → el boton se deshabilita con mensaje "Jornada completa".
 */
function accionesDisponibles(proximaAccion) {
  if (proximaAccion === 'entrada')         return ['entrada'];
  if (proximaAccion === 'inicio_almuerzo') return ['inicio_almuerzo', 'salida'];
  if (proximaAccion === 'fin_almuerzo')    return ['fin_almuerzo'];
  if (proximaAccion === 'salida')          return ['salida'];
  if (proximaAccion === 'cerrado' || proximaAccion == null) return []; // jornada completa, bloqueado
  return ['entrada'];
}

function colaboradorCard(c) {
  const bg       = avatarColor(c.nombreCompleto);
  const presente = c.estadoHoy === 'presente';
  const sinPin   = !c.tienePin;
  const acciones = sinPin ? [] : accionesDisponibles(c.proximaAccion);
  const jornadaCerrada = !sinPin && acciones.length === 0;
  // Primera acción disponible para mostrar como badge principal
  const accionPrincipal = acciones[0] || 'entrada';
  const { label: accionLabel, icon: accionIcon, cls: accionClass } = ACCION_INFO[accionPrincipal] || ACCION_INFO.entrada;

  const disabled = sinPin || jornadaCerrada;
  const disabledTitle = sinPin
    ? 'Sin PIN — configurar en Colaboradores'
    : (jornadaCerrada ? 'Jornada completa — vuelve manana' : '');

  return `
    <button class="kiosk-card ${sinPin ? 'kiosk-card--no-pin' : ''} ${jornadaCerrada ? 'kiosk-card--closed' : ''}"
            data-id="${c.id}"
            data-nombre="${c.nombreCompleto}"
            data-acciones="${acciones.join(',')}"
            ${disabled ? `disabled title="${disabledTitle}"` : ''}>
      <div class="kiosk-avatar" style="background:${bg}">${initials(c.nombreCompleto)}</div>
      <div class="kiosk-nombre">${c.nombreCompleto}</div>
      ${c.cargo ? `<div class="kiosk-cargo">${c.cargo}</div>` : ''}
      <div class="kiosk-estado ${presente ? 'estado-presente' : 'estado-ausente'}">
        ${presente ? '● Presente' : '○ Ausente'}
      </div>
      ${sinPin
        ? '<div class="kiosk-sin-pin">🔐 Sin PIN</div>'
        : jornadaCerrada
          ? '<div class="kiosk-accion accion-cerrada">✓ Jornada completa</div>'
          : `<div class="kiosk-accion ${accionClass}">${accionIcon} ${accionLabel}${acciones.length > 1 ? ' ▸' : ''}</div>`}
    </button>`;
}

/** Muestra un overlay con botones para elegir la acción cuando hay varias disponibles */
function showAccionSelector(container, colaboradorId, colaboradorNombre, acciones, businessId, onDone, kiosk = null) {
  const overlay = document.createElement('div');
  overlay.className = 'kiosk-pin-overlay';

  const botonesHTML = acciones.map(a => {
    const { label, icon, cls } = ACCION_INFO[a] || ACCION_INFO.entrada;
    return `<button class="kiosk-accion-sel-btn ${cls}" data-accion="${a}">${icon} ${label}</button>`;
  }).join('');

  overlay.innerHTML = `
    <div class="kiosk-pin-box kiosk-accion-sel-box">
      <div class="kiosk-pin-header">
        <span class="kiosk-pin-nombre">${colaboradorNombre}</span>
        <span style="color:var(--color-text-muted);font-size:14px">¿Qué deseas registrar?</span>
      </div>
      <div class="kiosk-accion-sel-btns">${botonesHTML}</div>
      <button class="kiosk-pin-cancel" id="accion-sel-cancel">Cancelar</button>
    </div>`;

  container.appendChild(overlay);

  overlay.querySelectorAll('.kiosk-accion-sel-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      overlay.remove();
      conAviso(container, { colaboradorId, colaboradorNombre, accion: btn.dataset.accion, kiosk },
        () => buildKeypad(container, colaboradorId, colaboradorNombre, btn.dataset.accion, businessId, onDone));
    });
  });
  overlay.querySelector('#accion-sel-cancel').onclick = () => overlay.remove();

  function onKeydown(e) {
    if (!document.contains(overlay)) { document.removeEventListener('keydown', onKeydown); return; }
    if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', onKeydown); }
  }
  document.addEventListener('keydown', onKeydown);
}

function buildKeypad(container, colaboradorId, colaboradorNombre, accion, businessId, onDone) {
  let pin = '';
  const { label: accionLabel, icon: accionIcon, cls: accionClass } = ACCION_INFO[accion] || ACCION_INFO.entrada;

  const overlay = document.createElement('div');
  overlay.className = 'kiosk-pin-overlay';
  overlay.innerHTML = `
    <div class="kiosk-pin-box">
      <div class="kiosk-pin-header">
        <span class="kiosk-pin-nombre">${colaboradorNombre}</span>
        <span class="kiosk-pin-accion ${accionClass}">${accionIcon} ${accionLabel}</span>
      </div>
      <div class="kiosk-pin-dots" id="pin-dots">
        ${'<span class="dot"></span>'.repeat(PIN_MAX)}
      </div>
      <div class="kiosk-pin-error" id="pin-error"></div>
      <div class="kiosk-keypad">
        <button class="kiosk-key" data-key="1">1</button>
        <button class="kiosk-key" data-key="2">2</button>
        <button class="kiosk-key" data-key="3">3</button>
        <button class="kiosk-key" data-key="4">4</button>
        <button class="kiosk-key" data-key="5">5</button>
        <button class="kiosk-key" data-key="6">6</button>
        <button class="kiosk-key" data-key="7">7</button>
        <button class="kiosk-key" data-key="8">8</button>
        <button class="kiosk-key" data-key="9">9</button>
        <button class="kiosk-key key-del" data-key="del">&larr;</button>
        <button class="kiosk-key" data-key="0">0</button>
        <button class="kiosk-key key-ok" data-key="ok">&check;</button>
      </div>
      <button class="kiosk-pin-cancel" id="pin-cancel">Cancelar</button>
    </div>`;

  container.appendChild(overlay);

  function updateDots() {
    const dots = overlay.querySelectorAll('.dot');
    dots.forEach((d, i) => d.classList.toggle('dot--filled', i < pin.length));
  }

  async function submit() {
    // DUVAN-PIN-T1: de 4 a 6 digitos (la regla del panel), no 5 clavado.
    if (!puedeEnviar(pin)) {
      const aviso = overlay.querySelector('#pin-error');
      if (aviso) aviso.textContent = AVISO_CORTO;
      return;
    }
    const btnOk = overlay.querySelector('.key-ok');
    btnOk.disabled = true;
    btnOk.textContent = '...';
    overlay.querySelector('#pin-error').textContent = '';

    // ApiBase devuelve { ok, data, error, code } — NO lanza excepciones en errores HTTP
    const res = await AsistenciaApiClient.marcar(businessId, { colaboradorId, pin, tipo: accion });

    if (!res.ok) {
      // Overlay PERMANECE — mostrar error, resetear PIN
      pin = '';
      updateDots();
      btnOk.disabled = false;
      btnOk.innerHTML = '&check;';
      const errEl = overlay.querySelector('#pin-error');
      if (errEl) {
        // PANEL-PIN-UNIFICADO-T1: con el nombre de quien teclea, y frenado / sin PIN / sin conexion dichos como son.
        errEl.textContent = respuestaDelPin(res, colaboradorNombre).texto;
        errEl.classList.add('shake');
        setTimeout(() => errEl.classList.remove('shake'), 600);
      }
      return;
    }

    // Éxito: cerrar overlay y mostrar confirmación
    const data = res.data?.data ?? res.data;
    overlay.remove();
    onDone({
      ok:      true,
      tipo:    data?.tipo    ?? accion,
      nombre:  data?.nombreColaborador ?? colaboradorNombre,
      mensaje: data?.mensaje ?? 'Marcación registrada',
    });
  }

  overlay.addEventListener('click', async e => {
    const key = e.target.closest('[data-key]')?.dataset.key;
    if (!key) return;
    if (key === 'del') { pin = pin.slice(0, -1); }
    else if (key === 'ok') { await submit(); }
    else if (/\d/.test(key)) {
      pin = agregarDigito(pin, key);
      if (seEnviaSolo(pin)) { updateDots(); await submit(); return; }
    }
    updateDots();
  });

  overlay.querySelector('#pin-cancel').onclick = () => overlay.remove();

  function onKeydown(e) {
    if (!document.contains(overlay)) { document.removeEventListener('keydown', onKeydown); return; }
    if (e.key >= '0' && e.key <= '9') { pin = agregarDigito(pin, e.key); updateDots(); if (seEnviaSolo(pin)) submit(); }
    else if (e.key === 'Backspace') { pin = pin.slice(0, -1); updateDots(); }
    else if (e.key === 'Enter') submit();
    else if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', onKeydown); }
  }
  document.addEventListener('keydown', onKeydown);
}

function confirmIconForTipo(tipo) {
  const map = {
    entrada:         '✅',
    inicio_almuerzo: '🍽',
    fin_almuerzo:    '▶',
    salida:          '👋'
  };
  return map[tipo] || '✅';
}

function showConfirmacion(container, { tipo, nombre, mensaje }, onDone) {
  const el = document.createElement('div');
  el.className = 'kiosk-confirm kiosk-confirm--' + tipo;
  el.innerHTML = `
    <div class="kiosk-confirm-icon">${confirmIconForTipo(tipo)}</div>
    <div class="kiosk-confirm-nombre">${nombre}</div>
    <div class="kiosk-confirm-msg">${mensaje}</div>
    <div class="kiosk-confirm-timer" id="confirm-timer">3</div>`;
  container.appendChild(el);
  let t = 3;
  const iv = setInterval(() => {
    t--;
    const timerEl = el.querySelector('#confirm-timer');
    if (timerEl) timerEl.textContent = t;
    if (t <= 0) { clearInterval(iv); el.remove(); onDone(); }
  }, 1000);
}

export async function renderMarcacion(container) {
  const businessId = getSelectedBusiness();

  const $nav = document.getElementById('nx-nav');
  const $topbar = document.getElementById('nx-topbar');
  if ($nav) $nav.style.display = 'none';
  if ($topbar) $topbar.style.display = 'none';

  container._cleanupBusinessListener = () => {
    if ($nav) $nav.style.display = '';
    if ($topbar) $topbar.style.display = '';
    container.className = '';
  };

  container.className = 'kiosk-container';
  container.innerHTML = `
    <div class="kiosk-header">
      <span class="kiosk-title">Control de Asistencia</span>
      <button class="kiosk-exit" id="kiosk-exit-btn">&times; Salir del kiosk</button>
    </div>
    <div class="kiosk-subtitle">Selecciona tu acción para marcar asistencia</div>
    <div class="kiosk-grid" id="kiosk-grid">
      <div class="kiosk-loading">Cargando colaboradores...</div>
    </div>
    <div class="kiosk-refresh-bar">
      <button class="kiosk-refresh-btn" id="kiosk-refresh">&#8635; Actualizar</button>
      <span class="kiosk-clock" id="kiosk-clock"></span>
    </div>`;

  function updateClock() {
    const el = document.getElementById('kiosk-clock');
    if (el) el.textContent = formatearFecha(new Date(), { hour: '2-digit', minute: '2-digit', second: '2-digit' }, { parte: 'hora' });
  }
  updateClock();
  const clockIv = setInterval(updateClock, 1000);
  const origCleanup = container._cleanupBusinessListener;
  container._cleanupBusinessListener = () => { clearInterval(clockIv); origCleanup?.(); };

  document.getElementById('kiosk-exit-btn').onclick = () => {
    // Cuentas de negocio (role=manager/client con business lockeado) vuelven al dashboard
    // limpio, NO a la vista admin /asistencia que tiene KPIs e historial.
    const locked = localStorage.getItem('nx_business_locked') === 'true';
    const role = localStorage.getItem('nx_role') || 'dev';
    const esNegocio = locked && role !== 'dev' && role !== 'admin';
    window.location.hash = esNegocio ? '#/' : '#/asistencia';
  };
  document.getElementById('kiosk-refresh').onclick = loadGrid;

  async function loadGrid() {
    if (!businessId) {
      document.getElementById('kiosk-grid').innerHTML =
        '<div class="kiosk-empty">Selecciona un negocio en el panel antes de abrir el kiosk.</div>';
      return;
    }
    try {
      const res = await AsistenciaApiClient.kiosk(businessId);
      const items = res.data?.data ?? res.data ?? [];
      const grid = document.getElementById('kiosk-grid');
      if (!grid) return;
      if (!items.length) {
        grid.innerHTML = '<div class="kiosk-empty">Sin colaboradores activos.</div>';
        return;
      }
      grid.innerHTML = items.map(colaboradorCard).join('');
      const porId = new Map(items.map((c) => [String(c.id ?? c.Id), c]));

      grid.querySelectorAll('.kiosk-card:not([disabled])').forEach(card => {
        card.addEventListener('click', () => {
          const { id, nombre, acciones: accionesStr } = card.dataset;
          const acciones = (accionesStr || 'entrada').split(',').filter(Boolean);
          const kiosk = porId.get(String(id)) || null;
          // Tras una marca exitosa, este aparato la recuerda (para el aviso de la siguiente).
          const alTerminar = (result) => { if (result.ok) { guardarReciente(id, result.tipo); showConfirmacion(container, result, loadGrid); } };

          if (acciones.length <= 1) {
            // Una sola acción posible → PIN directo
            conAviso(container, { colaboradorId: id, colaboradorNombre: nombre, accion: acciones[0] || 'entrada', kiosk },
              () => buildKeypad(container, id, nombre, acciones[0] || 'entrada', businessId, alTerminar));
          } else {
            // Múltiples opciones → selector de acción
            showAccionSelector(container, id, nombre, acciones, businessId, alTerminar, kiosk);
          }
        });
      });
    } catch (err) {
      const grid = document.getElementById('kiosk-grid');
      if (grid) grid.innerHTML = '<div class="kiosk-empty">Error cargando: ' + err.message + '</div>';
    }
  }

  loadGrid();
}
